import { InfoCircleOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';

import { FormItem, SelectField, SubmitButton, TextField } from 'componets/form';
import { Popover } from 'componets/ui';

import Utils from 'utils/utils';
import { language } from 'utils';
import { ClientRole, Paths, RequestStatus, Rules } from 'utils/constants';
import { FieldData, Object } from 'models';
import { selectCountries, selectUser } from 'selectors';

import { addTaxResidency } from 'features/registration/actions';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import styles from '../Register/index.module.scss';

const WORDS = [
	'selectCountryText',
	'countryResidencyText',
	'pIdText',
	'companyRegistrationNumberText',
	'submitText',
];

export const TaxResidencyForm = () => {
	const t = useTranslate(WORDS);

	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);

	const status = useAppSelector((state) => state.registration.status);
	const user = useAppSelector(selectUser);
	const countries = useAppSelector(selectCountries);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const isCompany = user.userType === ClientRole.COMPANY;

	const isEn = language === 'en';
	const ltRules = isEn ? [] : [Rules.LT_PID_NUMBER];

	const countriesOptions = useMemo(
		() =>
			countries
				.filter((item) => item.taxResidenceAvailable)
				.map((item) => {
					return {
						label: item.label,
						value: item.iso,
					};
				}) || [],
		[countries],
	);

	const currentCountry = useMemo(
		() => countries.find((item) => user.countryId === +item.id)?.iso,
		[countries, user.countryId],
	);

	const onFinish = (data: Object) => {
		dispatch(addTaxResidency(data))
			.unwrap()
			.then(() => {
				navigate(Paths.OVERVIEW);
			})
			.catch(({ errors }) => {
				Utils.injectFormErrors(fields, errors, setFields);
			});
	};

	return (
		<Form
			onFinish={onFinish}
			onFieldsChange={setFields}
			fields={fields}
			form={form}
			initialValues={{
				pIdNumber: isCompany ? user.idNumber : '',
				country: currentCountry,
			}}
		>
			<FormItem name="country" rules={[Rules.REQUIRED]} className="selectField">
				<SelectField
					selectText={t.selectCountryText}
					options={countriesOptions}
					placeholder={t.countryResidencyText}
					isFilter
				/>
			</FormItem>

			<FormItem
				validateTrigger="onSubmit"
				name="pIdNumber"
				rules={[Rules.REQUIRED, Rules.MAX_LENGTH('pIdNumber', 50), ...ltRules]}
				className={styles.pId}
			>
				<TextField
					label={isCompany ? t.companyRegistrationNumberText : t.pIdText}
					suffix={
						isEn &&
						!isCompany && (
							<Popover
								className={styles.popover}
								placement="topRight"
								content="Please note that your taxpayer ID may be your personal identification number."
							>
								<InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
							</Popover>
						)
					}
				/>
			</FormItem>

			<SubmitButton text={t.submitText} loading={status === RequestStatus.PENDING} />
		</Form>
	);
};
